import { TFunction } from "i18next";

export type InboxMessageChannel = "email" | "linkedin";

/**
 * Minimal structural shape of the conversation the label is derived from
 * (the lead's last sent/scheduled inbox conversation, stepId "n99").
 */
export interface InboxLabelConversation {
	message?: string | null;
	scheduled?: boolean | null;
	type?: string | null;
	senderEmail?: string | null;
}

/**
 * Resolves the channel of an inbox conversation. The backend stores the
 * channel in the conversation type ("mail_*" for email, "message_*" for
 * LinkedIn — see schedule.service sendMessageBasedOnScheduledMessage), so
 * prefer that and fall back to the sender fields. Defaults to LinkedIn to
 * preserve the previous label behavior for unknown shapes.
 */
const resolveInboxMessageChannel = (conversation?: InboxLabelConversation): InboxMessageChannel => {
	if (conversation?.type?.startsWith("mail")) {
		return "email";
	}

	if (conversation?.type?.startsWith("message")) {
		return "linkedin";
	}

	return conversation?.senderEmail ? "email" : "linkedin";
};

/**
 * Builds the channel-aware badge label for a manual inbox reply ("Reply from
 * Inbox - Email/LinkedIn", "Scheduled from Inbox - ...", or the follow-up
 * reply variants when the message matches the lead's reply2/reply3 drafts).
 * Returns null when there is no message to label.
 */
export const getInboxMessageLabel = ({
	lastSentData,
	reply2,
	reply3,
	stripHtml,
	t,
}: {
	lastSentData?: InboxLabelConversation;
	reply2?: string | null;
	reply3?: string | null;
	stripHtml: (html?: string) => string;
	t: TFunction;
}): string | null => {
	const message = lastSentData?.message ? stripHtml(lastSentData.message) : "";
	const reply2Text = reply2 ? stripHtml(reply2) : "";
	const reply3Text = reply3 ? stripHtml(reply3) : "";
	const isScheduled = lastSentData?.scheduled === true;
	const channel = resolveInboxMessageChannel(lastSentData);

	if (reply3Text && message === reply3Text) {
		return t(`outbox.follow_up_reply_2_${channel}`);
	}

	if (reply2Text && message === reply2Text) {
		return t(`outbox.follow_up_reply_1_${channel}`);
	}

	if (!message) {
		return null;
	}

	// Default: reply1, manually written, or any other inbox message
	if (channel === "email") {
		return isScheduled ? t("outbox.mail_scheduled") : t("outbox.mail_sent");
	}

	return isScheduled ? t("outbox.message_scheduled") : t("outbox.message_sent");
};
